import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { spring, paint, kick, rootPx } from './springs';

// The drop-down menu panel, shared by the Harbor bar and the classic one: a
// section's pages in columns under headings, unfolding below the bar. The
// colours are --hb-* tokens, so each bar dresses it in its own look.

// ---------- menu columns ----------
// Grouped sections: one column per group, merging the smallest neighbouring pair
// until there are at most three (two when pages sit beside the groups, as in
// Oversight). Flat sections: one column up to 10 items, then evenly balanced columns.
export function columnsFor(item) {
  const kids = item.children || [];
  if (kids.some((k) => k.children)) {
    const cols = kids.filter((g) => g.children && g.children.length).map((g) => [g]);
    // pages sitting beside the groups get a group of their own, headed with the section's name,
    // after the others: every column then starts with a heading
    const loose = kids.filter((k) => !k.children);
    if (loose.length) cols.push([{ key: `${item.key}-pages`, label: item.label, children: loose }]);
    const size = (c) => c.reduce((n, g) => n + g.children.length, 0);
    const most = loose.length ? 2 : 3;
    while (cols.length > most) {
      let best = 0;
      for (let i = 1; i < cols.length - 1; i++) if (size(cols[i]) + size(cols[i + 1]) < size(cols[best]) + size(cols[best + 1])) best = i;
      cols.splice(best, 2, cols[best].concat(cols[best + 1]));
    }
    return cols;
  }
  const n = kids.length;
  if (!n) return [];
  const per = Math.ceil(n / Math.ceil(n / 10));
  const out = [];
  for (let i = 0; i < n; i += per) out.push([{ key: `${item.key}-${i}`, label: null, children: kids.slice(i, i + per) }]);
  return out;
}

// ---------- open / close ----------
// barRef: the bar the panel hangs under. Returns the panel's state and the
// handlers a bar's buttons call.
export function useMenuPanel({ barRef, reduced }) {
  const [menu, setMenu] = useState(null); // { item, anchor, kind: 'hover' | 'click', at }
  const [closing, setClosing] = useState(false);
  const [itemsIn, setItemsIn] = useState(false);
  const menuRef = useRef(null);
  menuRef.current = menu;
  const panelRef = useRef(null);
  const timers = useRef({});

  const openMenu = (anchor, item, kind) => {
    const t = timers.current;
    clearTimeout(t.close); clearTimeout(t.fold);
    if (menuRef.current && menuRef.current.item.key === item.key && !closing) return;
    setClosing(false); setItemsIn(false);
    setMenu({ item, anchor, kind, at: performance.now() });
  };
  const closeMenu = () => {
    const t = timers.current, m = menuRef.current;
    if (!m) return;
    clearTimeout(t.items);
    if (reduced) { setMenu(null); return; }
    setClosing(true);
    t.fold = setTimeout(() => { if (menuRef.current === m) { setMenu(null); setClosing(false); } }, 175);
  };
  // After a menu item is clicked: gone at once.
  const closeNow = () => { const t = timers.current; clearTimeout(t.items); clearTimeout(t.close); setMenu(null); setClosing(false); };
  const cancelClose = () => clearTimeout(timers.current.close);
  const scheduleClose = () => { const t = timers.current; clearTimeout(t.open); clearTimeout(t.close); t.close = setTimeout(closeMenu, 260); };
  const hoverOpen = (anchor, item) => { const t = timers.current; clearTimeout(t.close); clearTimeout(t.open); t.open = setTimeout(() => openMenu(anchor, item, 'hover'), 55); };
  // A click right after hover opened it keeps it open; otherwise click toggles.
  const clickToggle = (anchor, item) => {
    const m = menuRef.current;
    if (m && m.item.key === item.key && !closing) { if (performance.now() - m.at > 400) closeMenu(); }
    else openMenu(anchor, item, 'click');
  };
  const isOpen = (key) => !!(menu && menu.item.key === key && !closing);

  // Place the panel under its anchor before it paints, then let the items swing in.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!menu || !panel) return;
    panel.style.left = '0px'; panel.style.width = ''; // measure unsqueezed
    const vw = document.documentElement.clientWidth;
    const r = menu.anchor.getBoundingClientRect(), cx = r.left + r.width / 2;
    // offsetWidth is the layout width: the opening animation starts at scale(.12), and a
    // transformed measurement would lock the panel to a sliver (one column, off screen).
    const pw = Math.min(panel.offsetWidth + 1, vw - 24);
    const left = Math.max(12, Math.min(vw - pw - 12, cx - pw / 2));
    const top = barRef.current.getBoundingClientRect().bottom + .5 * rootPx();
    panel.style.width = pw + 'px';
    panel.style.left = left + 'px';
    panel.style.top = top + 'px';
    panel.style.setProperty('--ox', (cx - left) + 'px');
    panel.style.setProperty('--bridge', (top - r.bottom + 4) + 'px');
    panel.querySelector('.hb-panel-scroll').style.maxHeight = (window.innerHeight - top - 12) + 'px';
    const mis = [...panel.querySelectorAll('.hb-mi')];
    mis.forEach((m) => { const s = spring(m, .15, .76); s.vy = 0; s.ty = 0; s.y = reduced ? 0 : -5; paint(s); });
    timers.current.items = setTimeout(() => {
      setItemsIn(true);
      mis.forEach((m, i) => setTimeout(() => kick(spring(m), reduced), Math.min(i, 14) * 16));
    }, reduced ? 0 : 190);
  }, [menu, reduced]); // eslint-disable-line react-hooks/exhaustive-deps

  // Escape and clicks outside close the panel.
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') { const a = menuRef.current?.anchor; closeMenu(); a?.focus(); } };
    const onDown = (e) => {
      const m = menuRef.current;
      if (m && !panelRef.current?.contains(e.target) && !m.anchor.contains(e.target)) closeMenu();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('pointerdown', onDown); };
  });

  useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  return { menu, closing, itemsIn, panelRef, menuRef, openMenu, closeNow, cancelClose, scheduleClose, hoverOpen, clickToggle, isOpen };
}

// ---------- the panel ----------
// onItemEnter / onItemLeave: extra hover work (the Harbor bar's hologram).
// onPicked: after an item is clicked and the panel has closed.
export function MenuPanel({ panel, selected, onNavigate, reduced, className = '', onItemEnter, onItemLeave, onPicked }) {
  const { menu, closing, itemsIn, panelRef, menuRef } = panel;
  if (!menu) return null;
  const { item } = menu;
  return (
    <div
      key={item.key}
      ref={panelRef}
      className={`hb-panel${className ? ` ${className}` : ''} ${closing ? 'hb-closing' : 'hb-open'}${itemsIn ? ' hb-items-in' : ''}`}
      role="menu"
      // when it opened: Northern Lights' aurora carries on from there instead of starting over
      style={{ '--nl-t': (menu.at / 1000).toFixed(2) }}
      onPointerEnter={panel.cancelClose}
      onPointerLeave={() => { if (menuRef.current?.kind === 'hover') panel.scheduleClose(); }}
    >
      <i className="hb-hud" />
      <div className="hb-panel-scroll">
        <div className="hb-cols">
          {columnsFor(item).map((col) => (
            <div className="hb-col" key={col.map((g) => g.key).join('+')}>
              {col.map((g, gi) => (
                <React.Fragment key={g.key}>
                  {gi > 0 && <div className="hb-grp-gap" />}
                  {g.label && <div className="hb-grp-h">{g.label}</div>}
                  {g.children.map((c) => (
                    <a
                      key={c.key}
                      href={c.action ? '#' : c.key}
                      role="menuitem"
                      className={`hb-mi${selected.has(c.key) ? ' hb-on' : ''}`}
                      onPointerEnter={(e) => { const s = spring(e.currentTarget, .15, .76); s.vy -= 1.1; kick(s, reduced); onItemEnter?.(item, c); }}
                      onPointerLeave={onItemLeave}
                      onClick={(e) => {
                        if (c.action) { e.preventDefault(); panel.closeNow(); onPicked?.(); c.action(); return; }
                        // let ctrl/cmd/shift/middle clicks open a new tab as links normally do
                        if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                        e.preventDefault(); panel.closeNow(); onPicked?.(); onNavigate(c.key);
                      }}
                    >
                      {c.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
